/**
 * Parsing for the `location_type` property, shared by every reader of it —
 * the LocationTypeInput select and the org scheduling defaults that seed new
 * booking links. Kept beside `workingHours.ts` (the same kind of property
 * parser) so the two consumers cannot grow divergent legacy-value handling.
 */

export type LocationTypeKind =
  | 'google_meet'
  | 'microsoft_teams'
  | 'zoom'
  | 'phone'
  | 'in_person'
  | 'custom';

export const LOCATION_OPTIONS: { id: LocationTypeKind; label: string }[] = [
  { id: 'google_meet', label: 'Google Meet' },
  { id: 'microsoft_teams', label: 'Microsoft Teams' },
  { id: 'zoom', label: 'Zoom' },
  { id: 'phone', label: 'Phone call' },
  { id: 'in_person', label: 'In person' },
  { id: 'custom', label: 'Custom' },
];

const VALID_KINDS = new Set<string>(LOCATION_OPTIONS.map(o => o.id));

function normalizeKind(raw: string): LocationTypeKind {
  return VALID_KINDS.has(raw) ? (raw as LocationTypeKind) : 'google_meet';
}

/**
 * Parse a location type value from the property store. Handles:
 * - Plain strings: 'google_meet', 'zoom', etc. (canonical format)
 * - Legacy JSON: '{"type":"google_meet"}' (backward compat)
 * - null/undefined → 'google_meet'
 */
export function parseLocationTypeKind(value: unknown): LocationTypeKind {
  if (value == null) {
    return 'google_meet';
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown;
      if (typeof parsed === 'object' && parsed !== null && 'type' in parsed) {
        return normalizeKind(String((parsed as Record<string, unknown>).type));
      }
    } catch {
      // Not JSON — treat as bare kind string
    }
    return normalizeKind(value);
  }

  if (typeof value === 'object' && 'type' in value) {
    return normalizeKind(String((value as Record<string, unknown>).type));
  }

  return 'google_meet';
}
